import { createTheme } from "@mui/material";
import { colors } from "./colors";
import BiNeo from "../fonts/BiNeoIndustrial-Regular.ttf";
import AndesNeueOTF from "../fonts/AndesNeue-Medium.otf";
import LatoTTF from "../fonts/Lato-Medium.ttf";

export const centralSitesTheme = createTheme({
  palette: {
    primary: {
      main: colors.primary,
      contrastText: colors.white.main,
    },
    secondary: {
      main: colors.secondary,
      contrastText: colors.white.main,
    },
    error: {
      main: colors.red.light,
    },
    success: {
      main: colors.green.main,
      contrastText: colors.white.main,
    },
    warning: {
      main: colors.yellow.main,
    },
  },
  typography: {
    useNextVariants: true,
    htmlFontSize: 12,
    fontWeightLight: 300,
    fontFamily: ["BiNeo", "AndesNeue,LatoTTF"].join(","),
    button: {
      textTransform: "capitalize",
    },
    h1: {
      fontSize: "2rem",
      lineHeight: "2.45rem",
      fontWeight: 700,
      letterSpacing: 0,
      color: colors.primary,
      fontFamily: "BiNeo",
    },
    h2: {
      fontSize: "1.5rem",
      lineHeight: "2.45rem",
      fontWeight: 700,
      letterSpacing: 0,
      color: colors.primary,
      fontFamily: "BiNeo",
    },
    h3: {
      fontSize: "1.5rem",
      lineHeight: "2.5rem",
      fontWeight: "bold",
      color: colors.primary,
      fontFamily: "BiNeo",
    },
    h4: {
      fontSize: "1rem",
      lineHeight: "2.45rem",
      fontWeight: 700,
      letterSpacing: 0,
      color: colors.white.main,
      fontFamily: "BiNeo",
    },
    h5: {
      fontSize: "1rem",
      lineHeight: "2.45rem",
      fontWeight: 700,
      letterSpacing: 0,
      color: colors.secondary,
      fontFamily: "BiNeo",
    },
    h6: {
      fontSize: "1.25rem",
      lineHeight: "2.45rem",
      fontWeight: 400,
      letterSpacing: 0,
      color: colors.gray.textDark,
      fontFamily: "BiNeo",
    },
    body1: {
      color: colors.primary,
      fontFamily: "BiNeo",
    },
    body2: {
      color: colors.gray.dark,
      fontSize: "1rem",
    },
  },
  components: {
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.primary,
          color: colors.white,
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        html: [
          {
            "@font-face": {
              fontFamily: "BiNeo",
              src: `url(${BiNeo}) format('truetype')`,
            },
          },
          {
            "@font-face": {
              fontFamily: "AndesNeue",
              src: `url(${AndesNeueOTF}) format('truetype')`,
            },
          },
          {
            "@font-face": {
              fontFamily: "Lato",
              src: `url(${LatoTTF}) format('truetype')`,
            },
          },
        ],
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          paddingTop: "0.75rem",
          paddingBottom: "0.75rem",
          borderRadius: "0.75rem",
          "&:hover": {
            backgroundColor: colors.yellow.main,
            color: colors.primary,
            borderColor: colors.yellow.main,
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          background: colors.white.main,
          borderRadius: "1rem",
          color: colors.gray.main,
          "& .MuiOutlinedInput-root": {
            borderRadius: "1rem",
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: "1rem",
          color: colors.gray.main,
          "& .Mui-disabled": {
            backgroundColor: colors.gray.light,
            borderRadius: "1rem",
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:nth-of-type(even)": {
            backgroundColor: colors.gray.light,
          },
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          "&:hover": {
            cursor: "pointer",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          color: colors.gray.dark,
          backgroundColor: colors.white.main,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          color: colors.white.main,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          height: "50px",
          borderRadius: "1.25rem",
          paddingLeft: "1rem",
          paddingRight: "1rem",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: colors.gray.main,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          backgroundColor: colors.gray.light,
          alignItems: "center !important",
          justifyContent: "space-between !important",
          fontSize: "1rem",
          textTransform: "none !important",
          height: "124px",
          textAlign: "start !important",
          "&.Mui-selected": {
            backgroundColor: colors.secondary,
            color: colors.primary,
            fontWeight: "bold",
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          display: "none",
          backgroundColor: colors.secondary,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        container: {
          backgroundColor: `${colors.primary}80`,
        },
        paper: {
          borderRadius: "4rem",
          padding: "2rem 2rem",
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          "&.Mui-checked": {
            color: colors.secondary,
            "&:after": {
              backgroundColor: "red",
            },
          },
        },
      },
    },
  },
});
